import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { friendlyAuthError } from "@/lib/auth-errors";
import { ContractorDisclaimer } from "@/components/contractors/ContractorDisclaimer";

export const Route = createFileRoute("/accept-contract/$token")({
  component: AcceptContractPage,
  head: () => ({
    meta: [
      { title: "Accept Contractor Partner invitation — MissionReady" },
      {
        name: "description",
        content:
          "Accept a MissionReady Contractor Partner invitation to set up your contractor organization, add your personnel and see contracted shifts.",
      },
      { property: "og:title", content: "Accept Contractor Partner invitation — MissionReady" },
      {
        property: "og:description",
        content: "Set up your contractor organization and see the sites and contracted shifts you are approved for.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
});

type Preview = {
  partnership_id: string;
  inviting_org_name: string;
  contractor_name: string;
  purpose: string | null;
  relationship_type: string;
  starts_on: string | null;
  ends_on: string | null;
  email: string;
  kind: "organization" | "individual" | "member";
  accepted_at: string | null;
  expires_at: string | null;
  site_names: string[] | null;
};

function AcceptContractPage() {
  const { token } = Route.useParams();
  const navigate = useNavigate();
  const { session, refreshMemberships, setCurrentOrgId } = useAuth();
  const [preview, setPreview] = useState<Preview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [orgName, setOrgName] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      const { data, error } = await (supabase.rpc as any)("get_contractor_invite", { _token: token });
      if (!alive) return;
      if (error) { setError("Data source unavailable. Try the link again shortly."); setLoading(false); return; }
      const row = Array.isArray(data) ? data[0] : data;
      if (!row) setError("This contractor invitation is invalid or has been withdrawn.");
      else { setPreview(row as Preview); setOrgName((row as Preview).contractor_name ?? ""); }
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [token]);

  const accept = async () => {
    if (!preview) return;
    setAccepting(true);
    const { data, error } = await (supabase.rpc as any)("accept_contractor_invite", {
      _token: token,
      _org_name: preview.kind === "organization" ? orgName.trim() || null : null,
    });
    setAccepting(false);
    if (error) return toast.error(friendlyAuthError(error));
    toast.success("Contractor partnership accepted");
    await refreshMemberships();
    // Contractor users work inside their own organization, never the inviting one.
    const { data: mine } = await supabase
      .from("contractor_members")
      .select("contractor_org_id")
      .eq("partnership_id", data as string)
      .limit(1)
      .maybeSingle();
    if (mine?.contractor_org_id) setCurrentOrgId(mine.contractor_org_id as string);
    navigate({ to: "/contractor" });
  };

  const expired = preview?.expires_at ? new Date(preview.expires_at) < new Date() : false;

  return (
    <div className="flex min-h-dvh items-center justify-center bg-hero px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-8 flex justify-center"><Logo /></div>
        <div className="rounded-2xl border border-border bg-card p-6 shadow-card sm:p-8">
          {loading ? (
            <p className="text-sm text-muted-foreground">Loading invitation…</p>
          ) : error || !preview ? (
            <>
              <h1 className="font-display text-2xl font-semibold">Invitation unavailable</h1>
              <p className="mt-2 text-sm text-muted-foreground">{error ?? "Unknown error"}</p>
              <Link to="/login" className="mt-6 inline-block text-sm text-primary hover:underline">Back to sign in</Link>
            </>
          ) : expired ? (
            <>
              <h1 className="font-display text-2xl font-semibold">Invitation expired</h1>
              <p className="mt-2 text-sm text-muted-foreground">
                This invitation expired on {new Date(preview.expires_at!).toLocaleDateString()}. Ask{" "}
                {preview.inviting_org_name} to send a new one.
              </p>
              <Link to="/login" className="mt-6 inline-block text-sm text-primary hover:underline">Back to sign in</Link>
            </>
          ) : (
            <>
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">
                Contractor Partner invitation
              </p>
              <h1 className="mt-2 font-display text-2xl font-semibold">
                {preview.kind === "member"
                  ? `Join ${preview.contractor_name}`
                  : `${preview.inviting_org_name} invited ${preview.contractor_name}`}
              </h1>

              <dl className="mt-5 space-y-2 text-sm">
                <div className="flex justify-between gap-3">
                  <dt className="text-muted-foreground">Inviting organization</dt>
                  <dd className="text-right font-medium">{preview.inviting_org_name}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-muted-foreground">Relationship</dt>
                  <dd className="text-right font-medium">
                    {preview.relationship_type === "ongoing" ? "Ongoing" : "Temporary"}
                    {preview.starts_on ? ` · from ${preview.starts_on}` : ""}
                    {preview.ends_on ? ` to ${preview.ends_on}` : ""}
                  </dd>
                </div>
                {preview.purpose ? (
                  <div className="flex justify-between gap-3">
                    <dt className="text-muted-foreground">Purpose</dt>
                    <dd className="text-right font-medium">{preview.purpose}</dd>
                  </div>
                ) : null}
                {preview.site_names?.length ? (
                  <div className="flex justify-between gap-3">
                    <dt className="text-muted-foreground">Site(s)</dt>
                    <dd className="text-right font-medium">{preview.site_names.join(", ")}</dd>
                  </div>
                ) : null}
                <div className="flex justify-between gap-3">
                  <dt className="text-muted-foreground">Sent to</dt>
                  <dd className="text-right font-medium">{preview.email}</dd>
                </div>
              </dl>

              {!session ? (
                <div className="mt-6 space-y-3">
                  <p className="text-sm">
                    Sign in or create an account with <strong>{preview.email}</strong> to accept.
                  </p>
                  <Link to="/login" search={{ next: `/accept-contract/${token}`, email: preview.email } as never} className="block">
                    <Button className="min-h-11 w-full">Sign in</Button>
                  </Link>
                  <Link to="/signup" search={{ next: `/accept-contract/${token}`, email: preview.email } as never} className="block">
                    <Button variant="outline" className="min-h-11 w-full">Create account</Button>
                  </Link>
                </div>
              ) : (
                <div className="mt-6 space-y-4">
                  {preview.kind === "organization" ? (
                    <div className="space-y-1.5">
                      <Label htmlFor="contractor-org-name">Your contractor organization name</Label>
                      <Input
                        id="contractor-org-name"
                        value={orgName}
                        onChange={(e) => setOrgName(e.target.value)}
                        placeholder="ABC Security LLC"
                        className="min-h-11"
                      />
                      <p className="text-xs text-muted-foreground">
                        You become the Contractor Organization Owner. This is your own company inside
                        MissionReady — you keep your personnel and records separate from{" "}
                        {preview.inviting_org_name}.
                      </p>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      You will join as a contractor member and see only the sites, contracted shifts and
                      post orders you are approved for.
                    </p>
                  )}
                  <Button className="min-h-12 w-full" onClick={accept} disabled={accepting}>
                    {accepting ? "Setting up…" : "Accept invitation"}
                  </Button>
                  <ContractorDisclaimer />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
